import { FlagFields } from '@modules/HappinessModule/interfaces/FlagFields';
import { NewProjectAssignmentData } from '@modules/HappinessModule/interfaces/NewProjectAssignmentData';

type FlagFormFieldsTypeMap = {
  [key in keyof Required<Omit<FlagFields, 'resolvedAt'>>]: {
    name: key;
    label: string;
    helperText?: string;
  };
};

export const NEW_PROJECT_ASSIGNMENT_FIELD_NAME: keyof FlagFields = 'newProjectAssignment';

type FlagFormFieldsNewProjectAssignmentTypeMap = {
  [key in keyof Required<NewProjectAssignmentData>]: {
    name: `${typeof NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.${key}`;
    label: string;
    helperText?: string;
  };
};

export const FlagFormFieldsNewProjectAssignment: FlagFormFieldsNewProjectAssignmentTypeMap = {
  requestedOS: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.requestedOS`,
    label: 'Requested OS',
  },
  requestedOSComment: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.requestedOSComment`,
    label: 'Requested OS comment',
  },
  project: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.project`,
    label: 'New project',
  },
  startDate: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.startDate`,
    label: 'Start date on a new project',
  },
  workload: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.workload`,
    label: 'Workload',
  },
  isSowSigned: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.isSowSigned`,
    label: 'SOW signed',
  },
  currentSeniority: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.currentSeniority`,
    label: 'Current seniority',
  },
  nearestEval: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.nearestEval`,
    label: 'Next evaluation',
  },
  evaluationNotes: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.evaluationNotes`,
    label: 'Notes about evaluation',
  },

  timeOffNotes: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.timeOffNotes`,
    label: 'Planned upcoming time off',
  },

  addHRSupport: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.addHRSupport`,
    label: 'Add HR Support to the email',
  },

  compensationAdjustmentAt: {
    name: `${NEW_PROJECT_ASSIGNMENT_FIELD_NAME}.compensationAdjustmentAt`,
    label: 'Compensation adjustments',
    helperText: 'Check if the engineer accepted a payment adjustment for this project. Can only be set when closing NewHome.',
  },
};

export const FlagFormFields: FlagFormFieldsTypeMap = {
  requestedOS: {
    name: 'requestedOS',
    label: 'Requested OS',
  },
  requestedOSComment: {
    name: 'requestedOSComment',
    label: 'Requested OS comment',
  },
  severity: {
    name: 'severity',
    label: 'Severity *',
  },
  pipLink: {
    name: 'pipLink',
    label: 'Performance improvement plan',
  },
  isEscalatedByClient: {
    name: 'isEscalatedByClient',
    label: 'The issue has been brought by client',
  },
  description: {
    name: 'description',
    label: 'Flag description',
  },
  whatWasDone: {
    name: 'whatWasDone',
    label: 'What was done?',
  },
  primaryTriggerType: {
    name: 'primaryTriggerType',
    label: 'Primary trigger',
  },
  primaryTrigger: {
    name: 'primaryTrigger',
    label: 'Primary reason',
  },
  primarySubReason: {
    name: 'primarySubReason',
    label: 'Primary sub-reason',
  },
  secondaryTriggerType: {
    name: 'secondaryTriggerType',
    label: 'Secondary trigger',
  },
  secondaryTrigger: {
    name: 'secondaryTrigger',
    label: 'Secondary reason',
  },
  secondarySubReason: {
    name: 'secondarySubReason',
    label: 'Secondary sub-reason',
  },
  departments: {
    name: 'departments',
    label: 'Affected projects',
  },
  suggestedActionItems: {
    name: 'suggestedActionItems',
    label: 'Suggested actions',
  },
  isNewHomeNeeded: {
    name: 'isNewHomeNeeded',
    label: 'NewHome needed',
  },
  isConfidential: {
    name: 'isConfidential',
    label: 'Confidential',
  },
  isReturnedFromSabbatical: {
    name: 'isReturnedFromSabbatical',
    label: 'Returned from sabbatical',
  },
  lastDayOnTheProject: {
    name: 'lastDayOnTheProject',
    label: 'Last day on the project',
  },
  newHomeStartDate: {
    name: 'newHomeStartDate',
    label: 'NewHome starting from',
  },
  newHomeAvailableFrom: {
    name: 'newHomeAvailableFrom',
    label: 'Date',
  },
  newHomeAvailableFromType: {
    name: 'newHomeAvailableFromType',
    label: 'Person is available from',
  },
  workload: {
    name: 'workload',
    label: 'NH workload',
  },
  pros: {
    name: 'pros',
    label: 'Pros (Hard and soft skills)',
    helperText: 'Please indicate the strongest person’s aspects, in what they are good at, at least 3 sentences',
  },
  cons: {
    name: 'cons',
    label: 'Cons (Hard and soft skills)',
    helperText:
      'Highlight person’s weaknesses and areas for improvement. Mention issues or accidents that influenced the project, client, or team',
  },
  softSkills: {
    name: 'softSkills',
    label: 'Accomplishments on a project',
    helperText: 'Add something that had a large impact on a project, and is worth noticing',
  },
  isAnotherOfferInvolved: {
    name: 'isAnotherOfferInvolved',
    label: 'Another offer involved',
  },
  resolutionNotes: {
    name: 'resolutionNotes',
    label: 'Resolution notes',
  },
  hiringType: {
    name: 'hiringType',
    label: 'Employment',
  },
  amountType: {
    name: 'amountType',
    label: 'Compensation amount',
  },
  status: {
    name: 'status',
    label: 'Status',
  },
  hrNotes: {
    name: 'hrNotes',
    label: 'HR notes',
  },
  offerAcceptanceStatus: {
    name: 'offerAcceptanceStatus',
    label: 'Offer Acceptance Status',
  },
  offerRejectionReason: {
    name: 'offerRejectionReason',
    label: 'Rejection reason *',
  },
  companyName: {
    name: 'companyName',
    label: 'Company',
  },
  role: {
    name: 'role',
    label: 'Title/Role',
  },
  compensation: {
    name: 'compensation',
    label: 'Compensation',
  },
  currencyType: {
    name: 'currencyType',
    label: 'Currency',
  },
  benefits: {
    name: 'benefits',
    label: 'Offer benefits details',
  },
  emailRecipients: {
    name: 'emailRecipients',
    label: 'Recipients',
  },
  isRecipientsDisabled: {
    name: 'isRecipientsDisabled',
    label: 'Start email thread',
  },
  isPassive: {
    name: 'isPassive',
    label: 'Passive',
  },
  isPrTeamInvolved: {
    name: 'isPrTeamInvolved',
    label: 'HR team involved',
  },
  assignedPR: {
    name: 'assignedPR',
    label: 'Assigned HR team member',
  },
  assignedTom: {
    name: 'assignedTom',
    label: 'Assigned Talent Operation manager',
  },
  newHomeEndDate: {
    name: 'newHomeEndDate',
    label: 'NewHome end date',
  },
  newHomeResolution: {
    name: 'newHomeResolution',
    label: 'Resolution',
  },
  starRate: {
    name: 'starRate',
    label: 'Star rate',
  },
  technicalSoftSkills: {
    name: 'technicalSoftSkills',
    label: 'Accomplishments on a project',
  },
  technicalPros: {
    name: 'technicalPros',
    label: 'Pros (Hard and soft skills)',
  },
  technicalCons: {
    name: 'technicalCons',
    label: 'Cons (Hard and soft skills)',
  },
  isSetNewResolutionDate: {
    name: 'isSetNewResolutionDate',
    label: 'Set new resolution date',
  },
  flagWatchers: {
    name: 'flagWatchers',
    label: 'Flag watchers',
  },
  nhEmailSentStatus: {
    name: 'nhEmailSentStatus',
    label: 'NewHome email sent status',
  },
  numOptionsShown: {
    name: 'numOptionsShown',
    label: '# of options shown',
  },
  numSubmissions: {
    name: 'numSubmissions',
    label: '# of submissions',
  },
  numTICIFailed: {
    name: 'numTICIFailed',
    label: '# TI/CI failed',
  },
  newProjectAssignment: {
    name: 'newProjectAssignment',
    label: 'New project found',
  },
  
  pto_incl: {
    name: 'pto_incl',
    label: 'Paid time off',
    helperText: 'Vacation, sick leaves and personal days',
  },
  paid_holidays: {
    name: 'paid_holidays',
    label: 'Paid holidays',
  },
  parental_leave: {
    name: 'parental_leave',
    label: 'Maternity or paternity leave',
  },
  health_ins: {
    name: 'health_ins',
    label: 'Health insurance',
  },
  wellness: {
    name: 'wellness',
    label: 'Wellness program',
    helperText: 'Gym membership, mental health program, etc.',
  },
  prof_dev: {
    name: 'prof_dev',
    label: 'Professional development program',
    helperText: 'Paid training platforms or economic support to cover education programs',
  },
  thirteenth_salary: {
    name: 'thirteenth_salary',
    label: '13th salary',
  },
  stock_opt: {
    name: 'stock_opt',
    label: 'Stock options',
  },
  bonuses: {
    name: 'bonuses',
    label: 'Bonuses',
  },
  compensationCompetitiveness: {
    name: 'compensationCompetitiveness',
    label: 'Compensation competitiveness',
  },
  benefitsNotProvided: {
    name: 'benefitsNotProvided',
    label: 'Benefits not provided',
  },
  companyNameNotProvided: {
    name: 'companyNameNotProvided',
    label: 'Company name not provided',
  },
  compensationNotProvided: {
    name: 'compensationNotProvided',
    label: 'Compensation not provided',
  },
  roleNotProvided: {
    name: 'roleNotProvided',
    label: 'Role not provided',
  },
  hiringTypeNotProvided: {
    name: 'hiringTypeNotProvided',
    label: 'Employement type not provided',
  },
  currencyTypeNotProvided: {
    name: 'currencyTypeNotProvided',
    label: 'Currency not provided',
  },
  amountTypeNotProvided: {
    name: 'amountTypeNotProvided',
    label: 'Compensation type not provided',
  },
};
